import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Canvas, useLoader, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import {
  CanvasTexture,
  DoubleSide,
  NoToneMapping,
  SRGBColorSpace,
  TextureLoader,
} from 'three';
import './Polygon360Tool.scss';

// Helper functions
function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Khong doc duoc anh 360.'));
    img.src = url;
  });
}

function clamp01(v) {
  return Math.min(1, Math.max(0, v));
}

function wrapU(u) {
  const w = u % 1;
  return w < 0 ? w + 1 : w;
}

async function decodeFileToRgba(file) {
  const previewUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(previewUrl);
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    if (width <= 0 || height <= 0)
      throw new Error('Kich thuoc anh khong hop le.');

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('Trinh duyet khong ho tro Canvas 2D.');

    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, width, height);
    const pixels = new Uint8ClampedArray(imageData.data.length);
    pixels.set(imageData.data);
    return {
      name: file.name,
      url: previewUrl,
      width,
      height,
      mime:
        file.type && file.type.startsWith('image/') ? file.type : 'image/png',
      pixels,
    };
  } catch (e) {
    URL.revokeObjectURL(previewUrl);
    throw e;
  }
}

function uvToCanvasPoint(pt, w, h) {
  return {
    x: wrapU(pt.u) * w,
    y: (1 - clamp01(pt.v)) * h,
  };
}

function uvDistance(a, b) {
  const du = Math.min(Math.abs(a.u - b.u), 1 - Math.abs(a.u - b.u));
  const dv = Math.abs(a.v - b.v);
  return Math.hypot(du, dv);
}

function drawPolygonPath(ctx, points, size, options = {}) {
  if (points.length < 2) return;
  const closeShape = options.closeShape ?? false;
  const fill = options.fill ?? false;
  const first = uvToCanvasPoint(points[0], size.width, size.height);

  ctx.beginPath();
  ctx.moveTo(first.x, first.y);
  for (let i = 1; i < points.length; i += 1) {
    const p = uvToCanvasPoint(points[i], size.width, size.height);
    ctx.lineTo(p.x, p.y);
  }
  if (closeShape && points.length >= 3) {
    ctx.closePath();
    if (fill) {
      ctx.fillStyle = 'rgba(94, 176, 255, 0.3)';
      ctx.fill();
    }
  }
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(94, 176, 255, 0.8)';
  ctx.stroke();
}

function drawPolygonPoints(ctx, points, size) {
  const radius = 4;
  for (const pt of points) {
    const p = uvToCanvasPoint(pt, size.width, size.height);
    ctx.beginPath();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.strokeStyle = 'rgba(94, 176, 255, 1)';
    ctx.lineWidth = 1.5;
    ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
    ctx.stroke();
  }
}

function drawClosingIndicator(ctx, point, size, canClose) {
  const p = uvToCanvasPoint(point, size.width, size.height);
  ctx.beginPath();
  ctx.strokeStyle = canClose
    ? 'rgba(255, 209, 102, 1)'
    : 'rgba(255, 209, 102, 0.4)';
  ctx.lineWidth = 2;
  ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
  ctx.stroke();
}

function CameraFovSync({ fov }) {
  const { camera } = useThree();
  useEffect(() => {
    if ('fov' in camera) {
      camera.fov = fov;
      camera.updateProjectionMatrix();
    }
  }, [camera, fov]);
  return null;
}

function PanoramaSphere({ textureUrl, overlayTexture, onSurfacePointerDown }) {
  const texture = useLoader(TextureLoader, textureUrl);
  texture.colorSpace = SRGBColorSpace;

  return (
    <>
      <mesh scale={[-1, 1, 1]} onPointerDown={onSurfacePointerDown}>
        <sphereGeometry args={[50, 96, 64]} />
        <meshBasicMaterial map={texture} side={DoubleSide} toneMapped={false} />
      </mesh>
      {overlayTexture && (
        <mesh scale={[-1, 1, 1]} onPointerDown={onSurfacePointerDown}>
          <sphereGeometry args={[49.98, 96, 64]} />
          <meshBasicMaterial
            map={overlayTexture}
            side={DoubleSide}
            transparent
            depthWrite={false}
            depthTest={false}
            toneMapped={false}
          />
        </mesh>
      )}
    </>
  );
}

// Main Polygon360Tool Component
const Polygon360Tool = ({ sceneData, onClose, onPolygonComplete }) => {
  const [panorama, setPanorama] = useState(null);
  const [polygons, setPolygons] = useState([]);
  const [draftPoints, setDraftPoints] = useState(null);
  const [selectedPolygonId, setSelectedPolygonId] = useState(null);
  const [viewFov, setViewFov] = useState(75);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const lastBlobRef = useRef(null);
  const closePointDistance = 0.02;

  const isDrawingPolygon = draftPoints !== null;

  // Load panorama from current scene data
  useEffect(() => {
    // Get the panorama URL from scene data - try hdImageUrl first, then imageUrl
    const panoramaUrl =
      sceneData?.PanoImage?.hdImageUrl || sceneData?.PanoImage?.imageUrl;
    const sceneName = sceneData?.name || 'panorama';

    if (panoramaUrl) {
      const loadFromUrl = async () => {
        try {
          setError(null);
          const response = await fetch(panoramaUrl);
          const blob = await response.blob();
          const file = new File([blob], `${sceneName}.jpg`, {
            type: blob.type,
          });
          const decoded = await decodeFileToRgba(file);
          if (lastBlobRef.current) URL.revokeObjectURL(lastBlobRef.current);
          lastBlobRef.current = decoded.url;
          setPanorama(decoded);
          setPolygons([]);
          setDraftPoints(null);
          setSelectedPolygonId(null);
          setViewFov(75);
        } catch (e) {
          console.error('[Polygon360Tool] Error loading panorama:', e);
          setError(e.message || 'Khong tai duoc anh 360.');
        }
      };
      loadFromUrl();
    } else {
      console.log('[Polygon360Tool] No panorama URL found in scene data');
    }
  }, [sceneData]);

  const onPanoramaFile = async (file) => {
    setError(null);
    if (!file) return;
    try {
      const decoded = await decodeFileToRgba(file);
      if (lastBlobRef.current) URL.revokeObjectURL(lastBlobRef.current);
      lastBlobRef.current = decoded.url;
      setPanorama(decoded);
      setPolygons([]);
      setDraftPoints(null);
      setSelectedPolygonId(null);
      setViewFov(75);
    } catch (e) {
      setError(e.message || 'Khong tai duoc anh 360.');
    }
  };

  const resolutionText = useMemo(() => {
    if (!panorama) return null;
    return `${panorama.width} x ${panorama.height}px`;
  }, [panorama]);

  const overlaySize = useMemo(() => {
    if (!panorama) return null;
    const width = Math.max(512, Math.min(2048, panorama.width));
    const height = Math.max(
      256,
      Math.round((width * panorama.height) / panorama.width)
    );
    return { width, height };
  }, [panorama]);

  const overlayBundle = useMemo(() => {
    if (!overlaySize) return null;
    const canvas = document.createElement('canvas');
    canvas.width = overlaySize.width;
    canvas.height = overlaySize.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    texture.needsUpdate = true;
    return { canvas, ctx, texture };
  }, [overlaySize]);

  useEffect(() => {
    return () => {
      overlayBundle?.texture.dispose();
    };
  }, [overlayBundle]);

  useEffect(() => {
    if (!overlayBundle || !overlaySize) return;
    const { ctx, canvas, texture } = overlayBundle;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (const polygon of polygons) {
      drawPolygonPath(ctx, polygon.points, overlaySize, {
        closeShape: true,
        fill: true,
      });
      drawPolygonPoints(ctx, polygon.points, overlaySize);
    }

    if (draftPoints && draftPoints.length > 0) {
      drawPolygonPath(ctx, draftPoints, overlaySize, {
        closeShape: false,
        fill: false,
      });
      drawPolygonPoints(ctx, draftPoints, overlaySize);

      const first = uvToCanvasPoint(
        draftPoints[0],
        overlaySize.width,
        overlaySize.height
      );
      ctx.beginPath();
      ctx.strokeStyle = '#ffd166';
      ctx.lineWidth = 1.5;
      ctx.arc(first.x, first.y, 5, 0, Math.PI * 2);
      ctx.stroke();

      if (draftPoints.length >= 3) {
        const lastPt = draftPoints[draftPoints.length - 1];
        const canClose =
          uvDistance(lastPt, draftPoints[0]) <= closePointDistance;
        drawClosingIndicator(ctx, draftPoints[0], overlaySize, canClose);
      }
    }

    texture.needsUpdate = true;
  }, [draftPoints, overlayBundle, overlaySize, polygons]);

  const selectedPolygon = useMemo(
    () => polygons.find((p) => p.id === selectedPolygonId) || null,
    [polygons, selectedPolygonId]
  );

  const startPolygon = () => {
    setError(null);
    setDraftPoints([]);
    setSelectedPolygonId(null);
  };

  const cancelPolygon = () => {
    setDraftPoints(null);
  };

  const finalizeDraftPolygon = useCallback(() => {
    setDraftPoints((prev) => {
      if (!prev || prev.length < 3) return prev;
      const id = `poly_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`;
      const newPolygon = { id, points: prev };

      // Call callback if provided
      if (onPolygonComplete) {
        onPolygonComplete(newPolygon);
      }

      setPolygons((existing) => [...existing, newPolygon]);
      setSelectedPolygonId(id);

      return null;
    });
  }, [onPolygonComplete]);

  const deleteSelectedPolygon = () => {
    if (!selectedPolygonId) return;
    setPolygons((prev) => prev.filter((p) => p.id !== selectedPolygonId));
    setSelectedPolygonId(null);
  };

  const clearAllPolygons = () => {
    setPolygons([]);
    setSelectedPolygonId(null);
  };

  const copyPolygonCoords = (polygon) => {
    const coords = polygon.points.map((pt) => ({
      u: Math.round(pt.u * 10000) / 10000,
      v: Math.round(pt.v * 10000) / 10000,
    }));
    const text = JSON.stringify(coords, null, 2);
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(polygon.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const exportAllPolygonData = () => {
    if (polygons.length === 0) return;
    const data = {
      version: 1,
      image: panorama
        ? { width: panorama.width, height: panorama.height }
        : null,
      polygons: polygons.map((p) => ({
        id: p.id,
        points: p.points.map((pt) => ({
          u: Math.round(pt.u * 10000) / 10000,
          v: Math.round(pt.v * 10000) / 10000,
        })),
      })),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const base = panorama?.name.replace(/\.[^.]+$/, '') || 'polygons';
    a.href = url;
    a.download = `${base}_polygons.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
  };

  const handleSurfacePointerDown = (e) => {
    e.stopPropagation();
    const uv = e.uv;
    if (!uv) return;
    const point = { u: wrapU(uv.x), v: clamp01(uv.y) };

    if (isDrawingPolygon) {
      let shouldFinalize = false;
      setDraftPoints((prev) => {
        const curr = prev || [];
        if (
          curr.length >= 3 &&
          uvDistance(point, curr[0]) <= closePointDistance
        ) {
          shouldFinalize = true;
          return curr;
        }
        return [...curr, point];
      });
      if (shouldFinalize) {
        finalizeDraftPolygon();
      }
      return;
    }

    setSelectedPolygonId(null);
  };

  const onViewerWheel = (e) => {
    if (!panorama) return;
    e.preventDefault();
    const step = e.deltaY > 0 ? 2 : -2;
    setViewFov((prev) => Math.min(95, Math.max(8, prev + step)));
  };

  useEffect(() => {
    if (!isDrawingPolygon) return;
    const onKeyDown = (ev) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        finalizeDraftPolygon();
        return;
      }
      if (ev.key === 'Backspace' || ev.key === 'Delete') {
        ev.preventDefault();
        setDraftPoints((prev) => {
          if (!prev || prev.length === 0) return null;
          if (prev.length === 1) return null;
          return prev.slice(0, -1);
        });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [finalizeDraftPolygon, isDrawingPolygon]);

  return (
    <div className="polygon360-tool">
      <div className="polygon360-tool__header">
        <div className="polygon360-tool__header-left">
          <h2>Polygon 360 Tool</h2>
          <p>Draw polygons to get UV coordinates</p>
        </div>
        {onClose && (
          <button
            type="button"
            className="polygon360-tool__close"
            onClick={onClose}
            title="Close"
          >
            ✕
          </button>
        )}
      </div>

      <div className="polygon360-tool__controls">
        {/* Show scene info */}
        <span className="tag">{sceneData?.name || 'Current Scene'}</span>
        {panorama && <span className="tag">{resolutionText}</span>}
        {!panorama && !error && <span className="tag">Loading...</span>}

        <div style={{ marginLeft: 'auto' }} />

        {/* Option to load custom image */}
        <label className="btn btn--ghost" style={{ fontSize: '11px' }}>
          Load Custom
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={(e) =>
              onPanoramaFile(e.target.files ? e.target.files[0] : null)
            }
          />
        </label>

        <button
          type="button"
          className="btn"
          disabled={!panorama}
          onClick={isDrawingPolygon ? cancelPolygon : startPolygon}
        >
          {isDrawingPolygon ? 'Cancel' : '+ Draw Polygon'}
        </button>

        <button
          type="button"
          className="btn"
          disabled={polygons.length === 0}
          onClick={clearAllPolygons}
        >
          Clear All
        </button>

        <button
          type="button"
          className="btn btn--primary"
          disabled={polygons.length === 0}
          onClick={exportAllPolygonData}
        >
          Export JSON
        </button>
      </div>

      {error && <p className="status status--error">{error}</p>}

      <div className="polygon360-tool__viewer" onWheel={onViewerWheel}>
        {isDrawingPolygon && (
          <div className="polygon360-tool__hint">
            Click on sphere to add points. Click near first point or press Enter
            to complete.
          </div>
        )}

        {selectedPolygon && (
          <div className="polygon360-tool__panel">
            <h3>Polygon {selectedPolygon.id}</h3>
            <p className="polygon360-tool__points-count">
              {selectedPolygon.points.length} points
            </p>
            <pre className="polygon360-tool__coords">
              {JSON.stringify(
                selectedPolygon.points.map((pt) => ({
                  u: Math.round(pt.u * 10000) / 10000,
                  v: Math.round(pt.v * 10000) / 10000,
                })),
                null,
                2
              )}
            </pre>
            <button
              type="button"
              className="btn"
              onClick={() => copyPolygonCoords(selectedPolygon)}
            >
              {copiedId === selectedPolygon.id ? 'Copied!' : 'Copy Coordinates'}
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={deleteSelectedPolygon}
            >
              Delete
            </button>
          </div>
        )}

        {!panorama && (
          <div className="polygon360-tool__empty">
            Load an equirectangular image to start drawing polygons
          </div>
        )}

        {panorama && (
          <Canvas
            camera={{ position: [0, 0, 0.1], fov: 75 }}
            dpr={[1, 2]}
            flat
            gl={{
              toneMapping: NoToneMapping,
              outputColorSpace: SRGBColorSpace,
            }}
          >
            <CameraFovSync fov={viewFov} />
            <Suspense fallback={null}>
              <PanoramaSphere
                textureUrl={panorama.url}
                overlayTexture={overlayBundle?.texture || null}
                onSurfacePointerDown={handleSurfacePointerDown}
              />
            </Suspense>
            <OrbitControls
              enablePan={false}
              enableZoom={false}
              rotateSpeed={-0.25}
            />
          </Canvas>
        )}
      </div>

      {polygons.length > 0 && (
        <div className="polygon360-tool__list">
          <h4>Polygons ({polygons.length})</h4>
          {polygons.map((p) => (
            <div
              key={p.id}
              className={`polygon360-tool__list-item ${selectedPolygonId === p.id ? 'active' : ''}`}
              onClick={() => setSelectedPolygonId(p.id)}
            >
              {p.id} ({p.points.length} pts)
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Polygon360Tool;
